import { useQueryClient } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  Bell,
  BookOpen,
  Clock,
  FolderKanban,
  Gavel,
  MessagesSquare,
  Coffee,
  Home,
  LayoutDashboard,
  LogOut,
  Moon,
  NotebookPen,
  Plus,
  Search,
  Settings,
  Shield,
  Sparkles,
  Square,
  Sun,
  Timer,
  User as UserIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ErrorBoundary } from '@/components/error-boundary';
import { AssistantPanel } from '@/features/assistant/AssistantPanel';
import { CreateItemDialog } from '@/features/items/CreateItemDialog';
import { Avatar, Button, Kbd, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, Tip } from '@/components/ui';
import { errorMessage, http, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useAiStatus, useProjects, useTimer, useUnread } from '@/lib/queries';
import { CatchUpButton } from '@/features/ai/AiTools';
import { usePendingApprovals } from '@/features/approvals/ApprovalsPage';
import { useChannels } from '@/features/chat/ChatPage';
import { disconnectSocket } from '@/lib/socket';
import type { Item } from '@/lib/types';
import { useUi } from '@/lib/ui-store';
import { cn } from '@/lib/utils';

function NavItem({ to, icon: Icon, label, badge, end }: { to: string; icon: typeof Home; label: string; badge?: number; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn('flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-subtle hover:bg-muted hover:text-fg', isActive && 'bg-brand-soft font-medium text-brand hover:bg-brand-soft hover:text-brand')
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1 truncate">{label}</span>
      {!!badge && <span className="rounded-full bg-brand px-1.5 text-[10px] font-semibold text-brand-fg">{badge > 99 ? '99+' : badge}</span>}
    </NavLink>
  );
}

function TimerChip() {
  const { data } = useTimer();
  const qc = useQueryClient();
  const [now, setNow] = useState(Date.now());
  const running = data && 'startedAt' in data ? data : null;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  if (!running) return null;
  const sec = Math.floor((now - new Date(running.startedAt).getTime()) / 1000);
  const stop = async () => {
    try {
      const r = await http.post<{ durationMin: number }>('/timers/stop', {});
      toast.success(`Logged ${r.durationMin}m on ${running.item?.key}`);
      void qc.invalidateQueries({ queryKey: ['timer'] });
      void qc.invalidateQueries({ queryKey: ['item'] });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-xs">
      <Timer className="h-3.5 w-3.5 animate-pulse text-danger" />
      <Link to={`/items/${running.item?.key}`} className="font-medium hover:underline">
        {running.item?.key}
      </Link>
      <span className="tabular-nums text-subtle">
        {Math.floor(sec / 3600)}:{String(Math.floor((sec % 3600) / 60)).padStart(2, '0')}:{String(sec % 60).padStart(2, '0')}
      </span>
      <button onClick={stop} title="Stop timer and log time" className="rounded p-0.5 hover:bg-muted">
        <Square className="h-3 w-3 fill-current" />
      </button>
    </div>
  );
}

/** ⌘K command palette: go to item by key, search, navigate, create (§14 keyboard). */
function Palette() {
  const { palette, setPalette, openCreate } = useUi();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const { data: projects } = useProjects();
  useEffect(() => {
    if (!palette || q.trim().length < 2) return setResults([]);
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      http
        .get<{ data: Item[] }>(`/items${qs({ q, limit: 8 })}`, ctrl.signal)
        .then((r) => setResults(r.data))
        .catch(() => undefined);
    }, 150);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q, palette]);
  if (!palette) return null;
  const go = (path: string) => {
    setPalette(false);
    setQ('');
    navigate(path);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setPalette(false)}>
      <Command className="mx-auto mt-[12vh] w-[min(640px,calc(100vw-2rem))] overflow-hidden rounded-lg border border-border bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()} shouldFilter={false}>
        <Command.Input autoFocus value={q} onValueChange={setQ} placeholder="Search items, type a key (KBX-12), or a command…" className="h-12 w-full border-b border-border bg-transparent px-4 text-sm outline-none" />
        <Command.List className="max-h-[50vh] overflow-y-auto p-1.5">
          {/^[A-Za-z][A-Za-z0-9]+-\d+$/.test(q.trim()) && (
            <Command.Item onSelect={() => go(`/items/${q.trim().toUpperCase()}`)} className="cursor-pointer rounded px-2 py-2 text-sm aria-selected:bg-muted">
              Go to {q.trim().toUpperCase()}
            </Command.Item>
          )}
          {results.length > 0 && (
            <Command.Group heading="Items" className="text-xs text-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
              {results.map((i) => (
                <Command.Item key={i.id} value={i.key} onSelect={() => go(`/items/${i.key}`)} className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-fg aria-selected:bg-muted">
                  <span className="font-medium text-subtle">{i.key}</span>
                  <span className="truncate">{i.title}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
          <Command.Group heading="Actions" className="text-xs text-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
            {q.trim() && (
              <Command.Item onSelect={() => go(`/search?q=${encodeURIComponent(q)}`)} className="cursor-pointer rounded px-2 py-2 text-sm text-fg aria-selected:bg-muted">
                Search everything for “{q}”
              </Command.Item>
            )}
            <Command.Item
              onSelect={() => {
                setPalette(false);
                openCreate({ type: 'quick_task' });
              }}
              className="cursor-pointer rounded px-2 py-2 text-sm text-fg aria-selected:bg-muted"
            >
              Create quick task
            </Command.Item>
            <Command.Item onSelect={() => go('/notes')} className="cursor-pointer rounded px-2 py-2 text-sm text-fg aria-selected:bg-muted">
              Open today’s note
            </Command.Item>
            {(projects ?? []).map((p) => (
              <Command.Item key={p.id} onSelect={() => go(`/p/${p.key}/board`)} className="cursor-pointer rounded px-2 py-2 text-sm text-fg aria-selected:bg-muted">
                Go to {p.name} board
              </Command.Item>
            ))}
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  );
}

export function Layout() {
  const { user } = useAuth();
  const { data: unread } = useUnread();
  const { data: pendingApprovals } = usePendingApprovals();
  const { data: channels } = useChannels();
  const chatUnread = (channels ?? []).filter((c) => c.isMember && c.type !== 'announcements').reduce((s, c) => s + c.unread, 0);
  const { data: projects } = useProjects();
  const { data: ai } = useAiStatus();
  const { assistantOpen, toggleAssistant, openCreate, setPalette, setPageContext } = useUi();
  const location = useLocation();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  // Default page context; feature pages refine it (FR-AI-31).
  useEffect(() => {
    const m = /^\/p\/([A-Z0-9]+)/.exec(location.pathname);
    setPageContext({ route: location.pathname, projectKey: m?.[1] });
  }, [location.pathname, setPageContext]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /input|textarea|select/i.test((e.target as HTMLElement).tagName) || (e.target as HTMLElement).isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        toggleAssistant();
      } else if (!typing && !e.metaKey && !e.ctrlKey && e.key === 'c') {
        e.preventDefault();
        openCreate({ projectKey: /^\/p\/([A-Z0-9]+)/.exec(location.pathname)?.[1] });
      } else if (!typing && !e.metaKey && !e.ctrlKey && e.key === 'q') {
        e.preventDefault();
        openCreate({ type: 'quick_task' });
      } else if (e.key === 'Escape') setPalette(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.pathname, openCreate, setPalette, toggleAssistant]);

  const logout = async () => {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include', headers: { 'x-kanbix-csrf': '1' } }).catch(() => undefined);
    disconnectSocket();
    useAuth.getState().clear();
    navigate('/login');
  };
  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    try {
      localStorage.setItem('kbx-theme', next ? 'dark' : 'light');
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex h-full">
      <aside className="hidden w-56 shrink-0 flex-col border-r border-border bg-surface md:flex">
        <Link to="/" className="flex items-center gap-2 px-4 py-3.5">
          <img src="/icon.svg" alt="" className="h-6 w-6" />
          <span className="font-semibold tracking-tight">
            360 <span className="text-brand">Kanbix</span>
          </span>
        </Link>
        <nav className="scrollbar-thin flex-1 space-y-0.5 overflow-y-auto px-2 pb-4">
          <NavItem to="/" end icon={Home} label="My work" />
          <NavItem to="/inbox" icon={Bell} label="Inbox" badge={unread?.unread} />
          <NavItem to="/chat" icon={MessagesSquare} label="Chat" badge={chatUnread || undefined} />
          <NavItem to="/standups" icon={Coffee} label="Stand-ups" />
          <NavItem to="/approvals" icon={Gavel} label="Approvals" badge={pendingApprovals?.length || undefined} />
          <NavItem to="/notes" icon={NotebookPen} label="My notes" />
          <NavItem to="/time" icon={Clock} label="Time" />
          <NavItem to="/dashboards" icon={LayoutDashboard} label="Dashboards" />
          <NavItem to="/search" icon={Search} label="Search" />
          <NavItem to="/library" icon={BookOpen} label="Library" />
          <div className="px-2.5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-subtle">Projects</div>
          {(projects ?? []).map((p) => (
            <NavItem key={p.id} to={`/p/${p.key}`} icon={FolderKanban} label={`${p.name}`} />
          ))}
          <NavItem to="/projects" end icon={Plus} label="All projects" />
          {user?.orgRole === 'org_admin' && (
            <>
              <div className="px-2.5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-subtle">Admin</div>
              <NavItem to="/admin" icon={Shield} label="Admin console" />
            </>
          )}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
          <button onClick={() => setPalette(true)} className="flex h-8 w-full max-w-md items-center gap-2 rounded-md border border-border bg-bg px-2.5 text-sm text-subtle hover:border-brand/40">
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search or jump to…</span>
            <Kbd>⌘K</Kbd>
          </button>
          <div className="flex-1" />
          <TimerChip />
          {ai?.enabled && ai.features.catch_up !== false && <CatchUpButton scope={{ kind: 'me' }} size="icon" variant="ghost" />}
          <Tip content={<>Create (C) · Quick task (Q)</>}>
            <Button size="sm" onClick={() => openCreate({ projectKey: /^\/p\/([A-Z0-9]+)/.exec(location.pathname)?.[1] })}>
              <Plus className="h-3.5 w-3.5" /> Create
            </Button>
          </Tip>
          {ai?.features.assistant && (
            <Tip content={<>Kanbix Assistant (⌘J)</>}>
              <Button variant={assistantOpen ? 'secondary' : 'ghost'} size="icon" onClick={() => toggleAssistant()} aria-label="Kanbix Assistant">
                <Sparkles className="h-4 w-4 text-brand" />
              </Button>
            </Tip>
          )}
          <Menu>
            <MenuTrigger asChild>
              <button className="rounded-full" aria-label="Account">
                <Avatar name={user?.displayName} url={user?.avatarUrl} size={28} />
              </button>
            </MenuTrigger>
            <MenuContent>
              <div className="px-2 py-1.5">
                <div className="text-sm font-medium">{user?.displayName}</div>
                <div className="text-xs text-subtle">{user?.email}</div>
              </div>
              <MenuSeparator />
              <MenuItem onSelect={() => navigate('/profile')}>
                <UserIcon className="h-4 w-4" /> Profile & preferences
              </MenuItem>
              <MenuItem onSelect={toggleTheme}>
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {dark ? 'Light' : 'Dark'} theme
              </MenuItem>
              {user?.orgRole === 'org_admin' && (
                <MenuItem onSelect={() => navigate('/admin')}>
                  <Settings className="h-4 w-4" /> Admin console
                </MenuItem>
              )}
              <MenuSeparator />
              <MenuItem onSelect={logout}>
                <LogOut className="h-4 w-4" /> Sign out
              </MenuItem>
            </MenuContent>
          </Menu>
        </header>
        <div className="flex min-h-0 flex-1">
          <main className="scrollbar-thin min-w-0 flex-1 overflow-y-auto">
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>
          {assistantOpen && <AssistantPanel />}
        </div>
        <nav className="flex items-center justify-around border-t border-border bg-surface py-1.5 md:hidden">
          {[
            ['/', Home],
            ['/inbox', Bell],
            ['/notes', NotebookPen],
            ['/time', Clock],
            ['/projects', FolderKanban],
          ].map(([to, Icon]) => {
            const I = Icon as typeof Home;
            return (
              <NavLink key={to as string} to={to as string} end className={({ isActive }) => cn('rounded-md p-2 text-subtle', isActive && 'text-brand')}>
                <I className="h-5 w-5" />
              </NavLink>
            );
          })}
        </nav>
      </div>
      <Palette />
      <CreateItemDialog />
    </div>
  );
}
